import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, Monitor } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { groupAlertsByDevice } from '@/lib/groupAlertsByDevice'
import { cn } from '@/lib/utils'

function formatWhen(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function severityVariant(severity) {
  if (severity === 'Critical') return 'destructive'
  if (severity === 'Warning') return 'warning'
  return 'secondary'
}

function DeviceAlertGroup({ group, open, onToggle }) {
  const panelId = `alerts-panel-${group.key}`
  const headerId = `alerts-header-${group.key}`

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-muted/15">
      <header
        id={headerId}
        className="flex items-start gap-2 border-b border-border/70 bg-muted/30 px-3 py-3"
      >
        <button
          type="button"
          className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={panelId}
          title={open ? 'Collapse alerts' : 'Expand alerts'}
        >
          <ChevronDown
            className={cn('h-5 w-5 transition-transform', !open && '-rotate-90')}
            aria-hidden
          />
        </button>
        <Monitor className="mt-1.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          {group.agentId ? (
            <Link
              to={`/agents/${group.agentId}`}
              className="block font-mono text-lg font-semibold tracking-tight text-primary hover:underline"
            >
              {group.hostname}
            </Link>
          ) : (
            <p className="font-mono text-lg font-semibold tracking-tight text-foreground">
              {group.hostname}
            </p>
          )}
          {(group.subtitle || group.customerName) && (
            <p className="mt-0.5 text-sm text-muted-foreground">
              {[group.subtitle, group.customerName].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <Badge variant="outline" className="mt-0.5 shrink-0 tabular-nums">
          {group.alerts.length === 1 ? '1 alert' : `${group.alerts.length} alerts`}
        </Badge>
      </header>
      {open && (
        <ul id={panelId} role="region" aria-labelledby={headerId}>
          {group.alerts.map((alert) => (
            <li
              key={alert.atera_id}
              className="flex items-start justify-between gap-3 border-b border-border/50 px-4 py-2.5 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-snug">
                  {alert.title || 'Alert'}
                </p>
                {alert.message && (
                  <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                    {alert.message}
                  </p>
                )}
                {formatWhen(alert.created) && (
                  <p className="mt-1 text-xs text-muted-foreground/80">
                    {formatWhen(alert.created)}
                  </p>
                )}
              </div>
              <Badge variant={severityVariant(alert.severity)} className="shrink-0">
                {alert.severity || 'Unknown'}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export function RecentAlertsByDevice({ alerts }) {
  const groups = useMemo(() => groupAlertsByDevice(alerts), [alerts])
  const groupKeys = useMemo(() => groups.map((g) => g.key), [groups])

  // Large estates stay collapsed; small lists open so the first devices are visible.
  const [expanded, setExpanded] = useState(
    () => (groupKeys.length <= 5 ? new Set(groupKeys) : new Set()),
  )

  useEffect(() => {
    setExpanded(groupKeys.length <= 5 ? new Set(groupKeys) : new Set())
  }, [groupKeys.join('|')])

  function toggle(key) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function expandAll() {
    setExpanded(new Set(groupKeys))
  }

  function collapseAll() {
    setExpanded(new Set())
  }

  const expandedCount = expanded.size

  return (
    <div className="space-y-3">
      {groups.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={expandAll}>
            Expand all
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={collapseAll}>
            Collapse all
          </Button>
          <span className="text-xs text-muted-foreground">
            {expandedCount} of {groups.length} expanded
          </span>
        </div>
      )}
      <div className="max-h-[520px] space-y-4 overflow-y-auto pr-1">
        {groups.map((group) => (
          <DeviceAlertGroup
            key={group.key}
            group={group}
            open={expanded.has(group.key)}
            onToggle={() => toggle(group.key)}
          />
        ))}
      </div>
    </div>
  )
}
